import { useEffect, useState, type ReactNode } from 'react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiProvider } from '../api/ApiProvider';
import type { ApiClient } from '../api/client';
import { ApiError } from '../api/client';
import { isSubscriptionRequired } from '../api/errors';
import { useAuthStore } from '../auth/store';
import { useSubscriptionGate } from '../subscription/gate';
import { applyLocaleToDocument, useLocaleStore } from '../i18n';
import { ToastViewport } from '../ui';
import { applyTheme, useThemeStore } from './theme';

/** `subscription_required` (402) sur n'importe quelle requête : la garde de routes redirige vers « Choisir une offre ». */
function onApiError(err: unknown) {
  const org = useAuthStore.getState().orgId;
  if (org && isSubscriptionRequired(err)) useSubscriptionGate.getState().flag(org);
}

export function createQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({ onError: onApiError }),
    mutationCache: new MutationCache({ onError: onApiError }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        // Pas de nouvelle tentative sur les erreurs « métier » (4xx)
        retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      },
    },
  });
}

/** Providers globaux : react-query, ApiClient, langue/direction, thème, toasts. */
export function Providers({ children, client, queryClient }: { children: ReactNode; client?: ApiClient; queryClient?: QueryClient }) {
  const [qc] = useState(() => queryClient ?? createQueryClient());
  const locale = useLocaleStore((s) => s.locale);
  const theme = useThemeStore((s) => s.theme);

  useEffect(() => applyLocaleToDocument(locale), [locale]);
  useEffect(() => applyTheme(theme), [theme]);

  return (
    <QueryClientProvider client={qc}>
      <ApiProvider client={client}>
        {children}
        <ToastViewport />
      </ApiProvider>
    </QueryClientProvider>
  );
}
